import { forwardRef, useCallback, useMemo } from 'react';
import { useResultsDispatch } from '@context/ResultsContext';
import { useVendor, useVendorDispatch } from '@context/VendorContext';
import { Wizard, type WizardRefMethods } from '@goodfynd/forms';
import { Loading } from '@goodfynd/ui';
import useVendorBusinessInfo from '@hooks/useVendorBusinessInfo';
import useVendorMap from '@hooks/useVendorMap';
import { useRouter } from 'next/router';
import type { EventSyncRequest } from 'types/schedule';
import { dateUtil, eventUtil, vendorUtil } from 'utils/index';
import { getDashboardRoutePath } from 'utils/routes-util';

import config from '../../../../config';
import routes from '../../../../config/routes';
import { useScheduleApi } from '../../../../hooks/api';

import {
  StepOperatingDays,
  StepOperatingDaysSummary,
} from './StepOperatingDays';
import {
  StepOperatingHours,
  StepOperatingHoursSummary,
} from './StepOperatingHours';
import {
  StepOperatingLocation,
  StepOperatingLocationSummary,
} from './StepOperatingLocation';
import { StepOrdering, StepOrderingSummary } from './StepOrdering';
import type { ScheduleWizardFields, ScheduleWizardProps } from './types';
import {
  operatingDaysValidation,
  operatingHoursValidation,
  operatingLocationValidation,
  orderingValidation,
} from './validation';

export const ScheduleWizard = forwardRef(function ScheduleWizard(
  {
    initialData,
    onExit,
    vendorId: vendorIdProp,
    ...props
  }: ScheduleWizardProps,
  ref: React.ForwardedRef<WizardRefMethods | null>
) {
  const api = useScheduleApi();
  const { addNewSchedules } = useResultsDispatch();
  const { vendor, vendorId: vendorIdFromContext } = useVendor();
  const vendorId = vendorIdProp || vendorIdFromContext;
  const { map, isLoading: mapIsLoading } = useVendorMap({ vendorId });
  const { businessInfo, isLoading: businessInfoIsLoading } =
    useVendorBusinessInfo({ vendorId });
  const { refetchVendor } = useVendorDispatch();
  const router = useRouter();

  const existingScheduleDictionary = useMemo<{
    [key: string]: OperatingDayLocation[];
  }>(
    () =>
      vendorUtil.getExistingScheduleDictionary(
        map?.schedule?.dayLocations || []
      ),
    [map?.schedule?.dayLocations]
  );

  const getInitialData = useCallback((): Partial<ScheduleWizardFields> => {
    const currentDate = new Date();
    return {
      dates: [currentDate],
      start: dateUtil.setTime(currentDate, new Date(0, 0, 0, 11, 0, 0)),
      end: dateUtil.setTime(currentDate, new Date(0, 0, 0, 14, 0, 0)),
      menuId: vendor.defaultMenu,
      isPrivate: false,
      pickupCapacity: businessInfo.pickupCapacity,
      pickupCapacityOption: {
        value: businessInfo.pickupCapacity,
      },
      existingScheduleDictionary,
      vendorId,
      ...initialData,
    };
  }, [
    businessInfo.pickupCapacity,
    existingScheduleDictionary,
    initialData,
    vendor.defaultMenu,
    vendorId,
  ]);

  const onSuccess = useCallback(() => {
    onExit?.();
  }, [onExit]);

  if (mapIsLoading || businessInfoIsLoading) {
    return <Loading />;
  }

  return (
    <Wizard<ScheduleWizardFields, void>
      {...props}
      compact
      initialData={getInitialData()}
      onExit={onExit}
      ref={ref}
      showHelpIcon={false}
      onSubmit={async (data) => {
        const {
          dates,
          end,
          start,
          location,
          pickupCapacityOption,
          taxPercentOption,
          menuId,
          isPrivate,
        } = data;

        // Calculate valid and invalid date ranges
        const { ranges } = dateUtil.getDateRanges(dates || [], (date) =>
          vendorUtil.validateSchedule(
            {
              end: dateUtil.setTime(date, end as Date),
              start: dateUtil.setTime(date, start as Date),
            },
            existingScheduleDictionary[
              dateUtil.format(date, config.dateFormats.yearMonthDay)
            ]
          )
        );

        const validDays = ranges.reduce<Date[]>((days, { start, end }) => {
          if (!start) return days;
          if (!end) {
            days.push(new Date(start));
          } else {
            days.push(
              ...dateUtil.parseDateRange({
                start: new Date(start),
                end: new Date(end),
              })
            );
          }
          return days;
        }, []);

        const schedule: EventSyncRequest = {
          days:
            (end &&
              start &&
              location &&
              validDays.map((date) => ({
                display: `${dateUtil.format(
                  date,
                  config.dateFormats.weekDayMonthDayYearShort
                )} from ${eventUtil.getEventTimeLabel({ end, start })}`,
                end: dateUtil.setTime(date, end),
                start: dateUtil.setTime(date, start),
                pickupCapacity: pickupCapacityOption?.value,
                taxPercent: taxPercentOption?.value,
                location,
              }))) ||
            [],
          pickupCapacity: pickupCapacityOption?.value
            ? Number(pickupCapacityOption.value)
            : undefined,
          source: 'Manual Entry',
          taxPercent: taxPercentOption?.value
            ? Number(taxPercentOption.value)
            : undefined,
          menuId,
          isPrivate,
          ...location,
        };

        console.debug(
          'Syncing event dates: ',
          schedule.days?.map((eventItem) => eventItem)
        );

        const result = await api.addEvents(vendorId as string, schedule);
        if (location?.name) {
          refetchVendor();
        }

        const formattedNewSchedule = result.schedule.map((el) => {
          const coordinates = el?.pickupLocation?.position?.coordinates;
          const strCoordinates = coordinates
            ? `${coordinates.latitude},${coordinates.longitude}`
            : '';
          return {
            ...el,
            vendorId: vendorId as string,
            pickupLocation: {
              ...el?.pickupLocation,
              coordinates: strCoordinates,
              directions: strCoordinates
                ? `https://www.google.com/maps/dir/?api=1&destination=${strCoordinates}`
                : '',
              latitude: coordinates?.latitude,
              longitude: coordinates?.longitude,
            },
          };
        });

        addNewSchedules(formattedNewSchedule);

        await router.push(
          getDashboardRoutePath(routes.dashboard.schedule.home, vendor.nameId)
        );

        onSuccess();
      }}
      steps={[
        {
          title: 'Operating Days',
          schema: operatingDaysValidation,
          renderStep: () => <StepOperatingDays />,
          renderSummary: () => <StepOperatingDaysSummary />,
        },
        {
          title: 'Operating Location',
          schema: operatingLocationValidation,
          renderStep: () => <StepOperatingLocation />,
          renderSummary: () => <StepOperatingLocationSummary />,
        },
        {
          title: 'Operating Hours',
          schema: operatingHoursValidation,
          renderStep: () => <StepOperatingHours />,
          renderSummary: () => <StepOperatingHoursSummary />,
        },
        {
          title: 'Ordering',
          schema: orderingValidation,
          renderStep: () => <StepOrdering />,
          renderSummary: () => <StepOrderingSummary />,
        },
      ]}
    />
  );
});
